import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import type { AssetCard as AssetCardData } from '@/api/assets'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'
import { useLibraryStore } from '@/stores/libraryStore'

import { AssetCard, CARD_BODY_HEIGHT, CARD_BORDER, CARD_THUMB_ASPECT } from './AssetCard'

/**
 * The library grid: a **row-lane virtualiser** (one virtual item per row of
 * `columns` cards, not one per card) so 1,600 assets scroll at 60 fps with a
 * handful of DOM nodes, and the whole grid keeps a single tab stop.
 *
 * Keyboard model (WAI-ARIA roving tabindex over a `listbox`):
 *  - arrows move the cursor, `Home`/`End` jump to the ends;
 *  - `Shift+arrow` extends the range from the anchor;
 *  - `Space` toggles, `Enter` replaces the selection;
 *  - `Ctrl+A` selects the page.
 *
 * The document scrolls, not the grid (§8): the virtualiser measures against
 * the window, so there is no inner scroll container to trap a wheel.
 */

export interface AssetGridProps {
  items: AssetCardData[]
  /** Called when the last row comes into view (infinite paging). */
  onLoadMore?(): void
  hasMore?: boolean
  loadingMore?: boolean
  /** Minimum card width in px; the column count follows the container. */
  minCardWidth?: number
  className?: string
  'aria-label'?: string
}

const GAP = 16
const OVERSCAN = 4

/**
 * Estimated row height for a given card width. Cards have a 4:3 thumb, so a
 * card's height grows with the column width; a constant (the old 268 px) was
 * only right at one window width and let taller cards spill into the next row.
 * The estimate only seeds the virtualiser — every mounted row is measured.
 */
export function estimateRowHeight(cardWidth: number): number {
  return Math.round(cardWidth * CARD_THUMB_ASPECT) + CARD_BODY_HEIGHT + CARD_BORDER + GAP
}

export function AssetGrid({
  items,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  minCardWidth = 200,
  className,
  'aria-label': ariaLabel = 'Assets',
}: AssetGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [columns, setColumns] = useState(1)
  const [cardWidth, setCardWidth] = useState(minCardWidth)

  const selected = useLibraryStore((s) => s.selected)
  const rovingIndex = useLibraryStore((s) => s.rovingIndex)
  const select = useLibraryStore((s) => s.select)
  const selectMany = useLibraryStore((s) => s.selectMany)
  const setRoving = useLibraryStore((s) => s.setRoving)
  const setOrder = useLibraryStore((s) => s.setOrder)

  const uids = useMemo(() => items.map((a) => a.uid), [items])
  useEffect(() => {
    setOrder(uids)
  }, [uids, setOrder])

  // Column count from the measured width — no media queries, so the grid is
  // correct inside any panel, not just at the page's breakpoints.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const width = el.clientWidth
      if (width <= 0) return
      const cols = Math.max(1, Math.floor((width + GAP) / (minCardWidth + GAP)))
      setColumns(cols)
      setCardWidth((width - GAP * (cols - 1)) / cols)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [minCardWidth])

  const rowCount = Math.ceil(items.length / columns)
  // The document scrolls, not the grid (§8), so the virtualiser measures
  // against the window; `scrollMargin` is where this grid starts on the page.
  const rowEstimate = estimateRowHeight(cardWidth)
  const virtualiser = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowEstimate,
    overscan: OVERSCAN,
    scrollMargin: containerRef.current?.offsetTop ?? 0,
  })

  // Row heights depend on the column width; drop the measured cache when it
  // changes so rows are re-measured instead of positioned from stale sizes.
  useLayoutEffect(() => {
    virtualiser.measure()
  }, [virtualiser, columns, rowEstimate])

  const rows = virtualiser.getVirtualItems()

  // Infinite paging: ask for the next page once the last row is mounted.
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : undefined
  useEffect(() => {
    if (!hasMore || loadingMore || !onLoadMore) return
    if (lastRow && lastRow.index >= rowCount - 2) onLoadMore()
  }, [hasMore, loadingMore, onLoadMore, lastRow, rowCount])

  // Cards are addressed by uid, not by index: the row they sit in may be
  // re-created by the virtualiser between the keypress and the next frame.
  const focusCell = useCallback((uid: string) => {
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-uid="${CSS.escape(uid)}"]`)
    el?.focus()
  }, [])

  const moveTo = useCallback(
    (next: number, extend: boolean) => {
      const clamped = Math.max(0, Math.min(items.length - 1, next))
      const uid = items[clamped]?.uid
      if (!uid) return
      setRoving(clamped)
      select(uid, extend ? 'range' : 'replace')
      // The row may be virtualised out; scroll it in, then focus.
      virtualiser.scrollToIndex(Math.floor(clamped / columns), { align: 'auto' })
      requestAnimationFrame(() => focusCell(uid))
    },
    [columns, focusCell, items, select, setRoving, virtualiser],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const i = rovingIndex
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          moveTo(i + 1, e.shiftKey)
          break
        case 'ArrowLeft':
          e.preventDefault()
          moveTo(i - 1, e.shiftKey)
          break
        case 'ArrowDown':
          e.preventDefault()
          moveTo(i + columns, e.shiftKey)
          break
        case 'ArrowUp':
          e.preventDefault()
          moveTo(i - columns, e.shiftKey)
          break
        case 'Home':
          e.preventDefault()
          moveTo(0, e.shiftKey)
          break
        case 'End':
          e.preventDefault()
          moveTo(items.length - 1, e.shiftKey)
          break
        case 'a':
        case 'A':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            selectMany(uids)
          }
          break
        default:
          break
      }
    },
    [columns, items.length, moveTo, rovingIndex, selectMany, uids],
  )

  const onCardFocus = useCallback(
    (uid: string) => {
      const idx = uids.indexOf(uid)
      if (idx >= 0 && idx !== rovingIndex) setRoving(idx)
    },
    [rovingIndex, setRoving, uids],
  )

  if (items.length === 0) return null

  const cursor = Math.min(rovingIndex, items.length - 1)

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-multiselectable
      aria-label={ariaLabel}
      data-testid="asset-grid"
      onKeyDown={onKeyDown}
      className={cn('relative w-full', className)}
    >
      <div style={{ height: virtualiser.getTotalSize(), position: 'relative' }}>
        {rows.map((row) => {
          const start = row.index * columns
          const rowItems = items.slice(start, start + columns)
          return (
            <div
              key={row.key}
              // Measured, not assumed: whatever a card's real height is, the
              // next row starts below it, so rows can never overlap.
              ref={virtualiser.measureElement}
              data-index={row.index}
              data-row={row.index}
              className="absolute left-0 top-0 grid w-full"
              style={{
                transform: `translateY(${row.start - (virtualiser.options.scrollMargin ?? 0)}px)`,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: GAP,
                paddingBottom: GAP,
              }}
            >
              {rowItems.map((asset, col) => (
                <AssetCard
                  key={asset.uid}
                  asset={asset}
                  selected={selected.has(asset.uid)}
                  focused={start + col === cursor}
                  onSelect={select}
                  onFocus={onCardFocus}
                />
              ))}
            </div>
          )
        })}
      </div>

      {hasMore ? (
        <div className="flex justify-center py-6">
          <Button variant="secondary" onClick={() => onLoadMore?.()} loading={loadingMore}>
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  )
}
